<script>
    import { selectedItem } from "../../store/store.js";
    import { displayInt } from "../../utils/utils.js";
    import "./shared.css";

    // ============================================
    // COLOR INTERVAL CONFIGURATION
    // ============================================
    // Define color thresholds for different metrics
    // Based on rating criteria from boanalys-backend/app/rating.py
    const colorIntervals = {
        "Äkta förening": {
            label: "Äkta förening",
            levels: [
                {
                    value: true,
                    color: "green",
                    label: "Ja",
                    description: "Äkta bostadsrättsförening",
                },
                {
                    value: false,
                    color: "red",
                    label: "Nej",
                    description: "Oäkta bostadsrättsförening",
                },
            ],
        },
        "Äger marken": {
            label: "Äger marken",
            levels: [
                {
                    value: true,
                    color: "green",
                    label: "Ja",
                    description: "Föreningen äger marken",
                },
                {
                    value: false,
                    color: "red",
                    label: "Nej",
                    description: "Tomträtt",
                },
            ],
        },
        "Lån/kvm": {
            label: "Lån per kvm",
            levels: [
                {
                    max: 0,
                    color: "dark-green",
                    label: "0 kr/kvm",
                    description: "Skuldfri - Exceptionellt",
                },
                {
                    min: 1,
                    max: 4999,
                    color: "green",
                    label: "1 - 4 999 kr/kvm",
                    description: "Mycket låg skuld",
                },
                {
                    min: 5000,
                    max: 9999,
                    color: "yellow",
                    label: "5 000 - 9 999 kr/kvm",
                    description: "Medelhög skuld",
                },
                {
                    min: 10000,
                    max: 14999,
                    color: "orange",
                    label: "10 000 - 14 999 kr/kvm",
                    description: "Hög skuld",
                },
                {
                    min: 15000,
                    color: "red",
                    label: "≥ 15 000 kr/kvm",
                    description: "Mycket hög skuld",
                },
            ],
        },
        "Sparande/kvm": {
            label: "Sparande per kvm",
            levels: [
                {
                    min: 350,
                    color: "dark-green",
                    label: "> 350 kr/kvm",
                    description: "Väldigt bra",
                },
                {
                    min: 250,
                    max: 349,
                    color: "green",
                    label: "250 - 349 kr/kvm",
                    description: "Bra",
                },
                {
                    min: 150,
                    max: 249,
                    color: "yellow",
                    label: "150 - 249 kr/kvm",
                    description: "Medel",
                },
                {
                    min: 50,
                    max: 149,
                    color: "orange",
                    label: "50 - 149 kr/kvm",
                    description: "Svagt",
                },
                {
                    max: 49,
                    color: "red",
                    label: "< 50 kr/kvm",
                    description: "Mycket svagt/Negativt",
                },
            ],
        },
        "Avgift/kvm": {
            label: "Avgift per kvm",
            levels: [
                {
                    max: 600,
                    color: "green",
                    label: "< 600 kr/kvm",
                    description: "Låg avgift",
                },
                {
                    min: 600,
                    max: 900,
                    color: "yellow",
                    label: "600 - 900 kr/kvm",
                    description: "Måttlig avgift",
                },
                {
                    min: 900,
                    color: "orange",
                    label: "> 900 kr/kvm",
                    description: "Hög avgift",
                },
            ],
        },
        Energiklass: {
            label: "Energiklass",
            levels: [
                {
                    value: "A",
                    color: "dark-green",
                    label: "A",
                    description: "Mycket energieffektiv",
                },
                {
                    value: "B",
                    color: "green",
                    label: "B",
                    description: "Energieffektiv",
                },
                {
                    value: "C",
                    color: "yellow",
                    label: "C",
                    description: "Godkänd",
                },
                {
                    value: "D",
                    color: "yellow",
                    label: "D",
                    description: "Acceptabel",
                },
                {
                    value: "E",
                    color: "orange",
                    label: "E",
                    description: "Låg energieffektivitet",
                },
                {
                    value: "F",
                    color: "red",
                    label: "F",
                    description: "Mycket låg energieffektivitet",
                },
            ],
        },
        "Energikostnad/kvm": {
            label: "Energikostnad per kvm",
            levels: [
                {
                    max: 150,
                    color: "green",
                    label: "< 150 kr/kvm",
                    description: "Låga energikostnader",
                },
                {
                    min: 150,
                    max: 225,
                    color: "yellow",
                    label: "150 - 225 kr/kvm",
                    description: "Medel energikostnader",
                },
                {
                    min: 225,
                    color: "orange",
                    label: "> 225 kr/kvm",
                    description: "Höga energikostnader",
                },
            ],
        },
    };

    /**
     * Determines the color category for a metric based on its value and configured intervals
     * @param {string} title - The metric title (e.g., 'Lån/kvm', 'Energiklass')
     * @param {string} value - The formatted display value
     * @param {number|string} rawValue - The raw value (numeric or string like 'A', 'B', 'C')
     * @returns {string} - Color category: 'dark-green', 'green', 'yellow', 'orange', 'red', or 'neutral'
     */
    function getCardColor(title, value, rawValue) {
        if (rawValue === null || rawValue === undefined) {
            return "neutral";
        }

        const intervals = colorIntervals[title];
        if (!intervals || !intervals.levels) {
            return "neutral";
        }

        // Iterate through levels to find matching interval
        for (const level of intervals.levels) {
            // Check for exact value match (for string values like 'A', 'B', 'C')
            if (level.value !== undefined) {
                if (
                    rawValue === level.value ||
                    rawValue?.toString().toUpperCase() === level.value
                ) {
                    return level.color;
                }
                continue;
            }

            // Numeric range checks
            const hasMin = level.min !== undefined;
            const hasMax = level.max !== undefined;

            if (hasMin && hasMax) {
                // Range check: min <= value <= max
                if (rawValue >= level.min && rawValue <= level.max) {
                    return level.color;
                }
            } else if (hasMin && !hasMax) {
                // Only minimum: value >= min
                if (rawValue >= level.min) {
                    return level.color;
                }
            } else if (!hasMin && hasMax) {
                // Only maximum: value <= max
                if (rawValue <= level.max) {
                    return level.color;
                }
            }
        }

        return "neutral";
    }

    $: statCards = $selectedItem
        ? [
              $selectedItem.genuine_house_association !== null &&
              $selectedItem.genuine_house_association !== undefined
                  ? {
                        title: "Äkta förening",
                        value: $selectedItem.genuine_house_association
                            ? "Ja"
                            : "Nej",
                        unit: "",
                        color: getCardColor(
                            "Äkta förening",
                            $selectedItem.genuine_house_association
                                ? "Ja"
                                : "Nej",
                            $selectedItem.genuine_house_association,
                        ),
                    }
                  : null,
              $selectedItem.owns_the_land !== null &&
              $selectedItem.owns_the_land !== undefined
                  ? {
                        title: "Äger marken",
                        value: $selectedItem.owns_the_land ? "Ja" : "Nej",
                        unit: "",
                        color: getCardColor(
                            "Äger marken",
                            $selectedItem.owns_the_land ? "Ja" : "Nej",
                            $selectedItem.owns_the_land,
                        ),
                    }
                  : null,
              (() => {
                  let loansPerSqm = $selectedItem.loans_per_living_sqm;
                  if (
                      (loansPerSqm === null || loansPerSqm === undefined) &&
                      $selectedItem.total_amount_of_loans != null &&
                      $selectedItem.living_area_sqm != null &&
                      $selectedItem.living_area_sqm > 0
                  ) {
                      loansPerSqm = Math.round(
                          $selectedItem.total_amount_of_loans /
                              $selectedItem.living_area_sqm,
                      );
                  }
                  return loansPerSqm !== null && loansPerSqm !== undefined
                      ? {
                            title: "Lån/kvm",
                            value: displayInt(loansPerSqm),
                            unit: "",
                            color: getCardColor(
                                "Lån/kvm",
                                displayInt(loansPerSqm),
                                loansPerSqm,
                            ),
                        }
                      : null;
              })(),
              $selectedItem.living_fee_per_sqm !== null &&
              $selectedItem.living_fee_per_sqm !== null &&
              $selectedItem.living_fee_per_sqm !== undefined
                  ? {
                        title: "Avgift/kvm",
                        value: displayInt($selectedItem.living_fee_per_sqm),
                        unit: "",
                        color: getCardColor(
                            "Avgift/kvm",
                            displayInt($selectedItem.living_fee_per_sqm),
                            $selectedItem.living_fee_per_sqm,
                        ),
                    }
                  : null,
              $selectedItem.saving_per_sqm !== null &&
              $selectedItem.saving_per_sqm !== undefined
                  ? {
                        title: "Sparande/kvm",
                        value: displayInt($selectedItem.saving_per_sqm),
                        unit: "",
                        color: getCardColor(
                            "Sparande/kvm",
                            displayInt($selectedItem.saving_per_sqm),
                            $selectedItem.saving_per_sqm,
                        ),
                    }
                  : null,
              $selectedItem.nybyggnadsar
                  ? {
                        title: "Byggår",
                        value: $selectedItem.nybyggnadsar,
                        unit: "",
                        color: "neutral",
                    }
                  : null,
              $selectedItem.energiklass
                  ? {
                        title: "Energiklass",
                        value: $selectedItem.energiklass,
                        unit: "",
                        color: getCardColor(
                            "Energiklass",
                            $selectedItem.energiklass,
                            $selectedItem.energiklass,
                        ),
                    }
                  : null,
          ].filter((card) => card !== null)
        : [];
</script>

<div class="general-section">
    <div class="general-content">
        <div class="content-layout">
            <div class="left-column">
                <div class="summary-text">
                    <div class="header-card">
                        <h5>
                            <i class="fa-solid fa-building fa-sm icon-detail"
                            ></i> Översikt
                        </h5>
                    </div>
                    <p>{$selectedItem?.rating?.overall_rating?.reason || ""}</p>
                </div>

                <div class="org-info">
                    {#if $selectedItem?.org_number}
                        <div class="org-item" data-index="0">
                            <span class="org-label">Organisationsnummer</span>
                            <span class="org-value"
                                >{$selectedItem.org_number}</span
                            >
                        </div>
                    {/if}
                    {#if $selectedItem?.fastighetsbeteckning}
                        <div class="org-item" data-index="0">
                            <span class="org-label">Fastighetsbeteckning</span>
                            <span class="org-value"
                                >{$selectedItem.fastighetsbeteckning}</span
                            >
                        </div>
                    {/if}
                    {#if $selectedItem?.registry_date}
                        <div class="org-item" data-index="1">
                            <span class="org-label">Föreningen bildad</span>
                            <span class="org-value"
                                >{new Date($selectedItem.registry_date)
                                    .toISOString()
                                    .split("T")[0]}</span
                            >
                        </div>
                    {/if}
                    {#if $selectedItem?.municipality}
                        <div class="org-item" data-index="2">
                            <span class="org-label">Kommun</span>
                            <span class="org-value"
                                >{$selectedItem.municipality}</span
                            >
                        </div>
                    {/if}
                </div>
            </div>

            <div class="stats-section">
                <div class="atom-container">
                    <div class="atom-center">
                        <div class="grade-container">
                            <div class="grade">
                                {$selectedItem?.rating?.overall_rating
                                    ?.rating ?? "-"}
                            </div>
                            <div class="grade-label">av 100</div>
                        </div>
                    </div>
                    {#each statCards.slice(0, 8) as card, index}
                        {@const totalCards = statCards.length}
                        {@const angle = (360 / totalCards) * index}
                        {@const radius = 180}
                        {@const colorIndex = index % 8}
                        <div
                            class="atom-pill stat-card-{card.color}"
                            style="--angle: {angle}deg; --radius: {radius}px;"
                            data-color-index={colorIndex}
                        >
                            <div class="pill-content">
                                <h6>{card.title}</h6>
                                <p class="value">{card.value}</p>
                                <p class="unit">{card.unit}</p>
                            </div>
                        </div>
                    {/each}
                </div>
            </div>
        </div>
    </div>
</div>

<style>
    .general-section {
        width: 100%;
    }

    .general-content {
        background: white;
        backdrop-filter: blur(30px) saturate(120%);
        -webkit-backdrop-filter: blur(30px) saturate(120%);
        border-radius: 24px;
        border: none;
        padding: 2rem;
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.3),
            inset 0 -1px 0 rgba(255, 255, 255, 0.1),
            0 0 0 1px rgba(255, 255, 255, 0.15);
    }

    .content-layout {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 2rem;
        align-items: start;
    }

    .left-column {
        display: flex;
        flex-direction: column;
        gap: 2rem;
    }

    .summary-text {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        text-align: left;
    }

    .summary-header {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }

    .summary-divider {
        height: 3px;
        width: 80px;
        background: linear-gradient(
            90deg,
            var(--hover) 0%,
            var(--hover) 50%,
            rgba(236, 119, 106, 0.3) 80%,
            transparent 100%
        );
        border-radius: 3px;
        position: relative;
    }

    .summary-divider::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.2) 50%,
            transparent 100%
        );
        border-radius: 3px;
    }

    .summary-text p {
        margin: 0;
        color: #495057;
        line-height: 1.7;
        font-size: 1rem;
    }

    .org-info {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: 1rem;
        margin-top: auto;
    }

    .org-item {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding: 1.25rem 1.5rem;
        background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.1) 0%,
            rgba(255, 255, 255, 0.05) 100%
        );
        backdrop-filter: blur(30px) saturate(120%);
        -webkit-backdrop-filter: blur(30px) saturate(120%);
        border-radius: 20px;
        border: none;
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.3),
            inset 0 -1px 0 rgba(255, 255, 255, 0.1),
            0 0 0 1px rgba(255, 255, 255, 0.15);
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;
    }

    .org-item:hover {
        transform: translateY(-4px);
        box-shadow:
            0 12px 40px rgba(0, 0, 0, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.4),
            inset 0 -1px 0 rgba(255, 255, 255, 0.15),
            0 0 0 1px rgba(255, 255, 255, 0.2);
    }

    .org-label {
        font-size: 0.75rem;
        color: #666;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .org-value {
        font-size: 1.1rem;
        color: #1a1a1a;
        font-weight: 700;
        line-height: 1.2;
    }

    .stats-section {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .grade-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.25rem;
    }

    .grade-label {
        font-size: 0.75rem;
        color: #666;
        font-weight: 400;
        line-height: 1;
        margin-top: -25px;
    }

    @media (max-width: 1200px) {
        .content-layout {
            grid-template-columns: 1fr;
            gap: 2rem;
        }

        .stats-section {
            min-height: 400px;
        }

        .left-column {
            gap: 1.5rem;
        }
    }

    @media (min-width: 768px) and (max-width: 1024px) {
        .org-info {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    @media (max-width: 992px) {
        .general-content {
            padding: 1.5rem;
        }
    }

    @media (max-width: 767px) {
        .org-info {
            grid-template-columns: 1fr;
        }
    }
</style>
